import * as React from 'react';

import _ from 'lodash';
import { compose } from 'recompose';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import config from 'js/app/config';
import type { ActionContext } from 'js/lib/ActionContext';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, Link, Typography } from '@coursera/cds-core';

import Modal from 'bundles/phoenix/components/Modal';

import _t from 'i18n!nls/staff-management';

import 'css!./__styles__/InviteModal';

import PillList from './common/PillList';
import withNotifications from './common/notifications/withNotifications';
import type { StaffManagementStoreV2 as StaffManagementStoreClass, StaffMembershipType } from './store';
import { actions } from './store';
import withFluxibleContextAsProps from './withFluxibleContextAsProps';
import withShow from './withShow';

type Props = {
  /** Called when user clicks cancel button. */
  cancelOnClick?: () => void;
  /** If true, cancel button cannot be clicked (no callback). */
  cancelDisabled?: boolean;

  /** Called when user clicks confirm button. */
  confirmOnClick?: () => void;
  /** If true, confirm button cannot be clicked (no callback). */
  confirmDisabled?: boolean;

  /** User emails to be invited to become staff members. */
  emails?: Array<string>;

  /** One of the types supported by the staff-membership component. Modifies messaging shown. */
  type?: keyof typeof StaffMembershipType;

  /** Creates a new toast notification */
  notify: () => void;
};

/**
 * Blocking confirmation modal, displayed when letting a user know that some of their attempted
 * membership additions are to non-courserian users and asking if we wish to invite them?
 */
export const InviteModal = ({
  emails = [],
  cancelOnClick = _.noop,
  cancelDisabled = false,
  confirmOnClick = _.noop,
  confirmDisabled = false,
  type = 'partner' as const,
}: Props) => {
  let contentMessage;
  if (type === 'partner') {
    contentMessage = _t(
      'The following staff members need a Coursera account to add them to an institution. Click below to send an email invitation. They can also create one at the {signUpPage}.'
    );
  } else if (type === 'program') {
    contentMessage = _t(
      'The following staff members need a Coursera account to add them to a program. Click below to send an email invitation. They can also create one at the {signUpPage}.'
    );
  }

  const title = _t('Create Coursera Account');

  return (
    // At time of writing modal does not allow pass through of additional properties. To allow selection of modal components
    // we wrap entire contents in `data-e2e` tag.
    <div data-e2e={InviteModal.e2eName}>
      <Modal
        className="rc-InviteModal"
        // This gets dropped into an `aria-label` on the modal. This should be translated and in plain language.
        modalName={title}
        allowClose={false}
      >
        <Typography className="title" variant="h1" component="h2">
          {title}
        </Typography>

        <p className="content">
          <FormattedMessage
            // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
            message={contentMessage}
            numItems={emails.length}
            signUpPage={<Link href={`${config.url.base}?authMode=signup`}>{_t('Sign Up page')}</Link>}
          />
        </p>

        <PillList items={emails} />

        <div className="footer">
          <Button
            disabled={cancelDisabled}
            data-e2e="btn-cancel"
            onClick={cancelOnClick}
            className="button"
            variant="primary"
          >
            {_t('Cancel')}
          </Button>
          <Button
            disabled={confirmDisabled}
            onClick={confirmOnClick}
            data-e2e="btn-confirm"
            className="button"
            variant="primary"
          >
            {_t('Invite')}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

InviteModal.e2eName = 'staff-management-invite-modal';

export const storeConnector = (
  { StaffManagementStoreV2 }: { StaffManagementStoreV2: StaffManagementStoreClass },
  { executeAction, notify }: $TSFixMe
) => {
  const state = StaffManagementStoreV2.get();
  const users = state.ui?.inviteFlow?.users;
  const type = state.ui?.inviteFlow?.type;
  return {
    cancelOnClick: () => {
      return executeAction(actions.inviteFlow.prompt);
    },
    confirmOnClick: async () => {
      executeAction(actions.inviteFlow.confirm, { notify });
    },
    emails: users,
    show: !!users?.length,
    type,
  };
};

export default compose<Props, {}>(
  withFluxibleContextAsProps(),
  withNotifications(),
  connectToStores<Props, Props & ActionContext>(['StaffManagementStoreV2'], storeConnector),
  withShow()
)(InviteModal);
